import enLocale from "@fullcalendar/core/locales/en-gb";
import svLocale from "@fullcalendar/core/locales/sv";
import interactionPlugin from "@fullcalendar/interaction";
import momentPlugin from "@fullcalendar/moment";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import timeGridPlugin from "@fullcalendar/timegrid";
import firebase from "firebase/app";
import moment from "moment";

import { Dimmer, Grid, Label, Loader } from "semantic-ui-react";

import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useWindowSize from "react-use/lib/useWindowSize";

import { db } from "astrid-firebase";
import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import useCollectionDataByIds from "astrid-firestore/src/hooks/useCollectionDataByIds";
import useDocumentData from "astrid-firestore/src/hooks/useDocumentData";
import msToTime from "astrid-helpers/src/msToTime";

import Flex from "../../../../components/Flex/Flex";
import LoadingContext from "../../../ui/components/LoadingContext/LoadingContext";
import Section from "../../../ui/components/Section/Section";

import { useProfile } from "../../../authentication/state/profile";
import useHasRight from "../../../authorization/hooks/useHasRight";

import CalendarAvailabilityForm from "../../components/CalendarAvailabilityForm/CalendarAvailabilityForm";
import CalendarBookingAssistant from "../../components/CalendarBookingAssistant/CalendarBookingAssistant";
import CalendarFilterForm from "../../components/CalendarFilterForm/CalendarFilterForm";
import useCalendarFilterForm from "../../components/CalendarFilterForm/hooks/useCalendarFilterForm";
import CalendarHeaderToolbar from "../../components/CalendarHeaderToolbar/CalendarHeaderToolbar";
import useCalendarTimelineNavigation from "../../components/CalendarHeaderToolbar/hooks/useCalendarTimelineNavigation";
import CalendarNotificationModal from "../../components/CalendarNotificationModal/CalendarNotificationModal";
import CalendarResourceHeader from "../../components/CalendarResourceHeader/CalendarResourceHeader";
import CalendarSessionModal from "../../components/CalendarSessionModal/CalendarSessionModal";
import getCalendarBusinessHours from "../../utils/getCalendarBusinessHours";

import CalendarEventContent from "./components/CalendarEventContent";
import CalendarFilterFormModal from "./components/CalendarFilterFormModal";
import CalendarMoreLinkContent from "./components/CalendarMoreLinkContent";
import CalendarToast from "./components/CalendarToast";
import useCalendarBackgroundEvents from "./hooks/useCalendarBackgroundEvents";
import useCalendarEvents from "./hooks/useCalendarEvents";
import useCalendarOnChangeEvent from "./hooks/useCalendarOnChangeEvent";
import useCalendarOnCreateSession from "./hooks/useCalendarOnCreateSession";
import useCalendarResourceTypes from "./hooks/useCalendarResourceTypes";
import useCalendarSessions from "./hooks/useCalendarSessions";
import useCalendarStudioTime from "./hooks/useCalendarStudioTime";
import useFullCalendarProps from "./hooks/useFullCalendarProps";

export default function CalendarView({ organization, productionId }) {
	const ref = useRef();
	const { t, i18n } = useTranslation();
	const hasRight = useHasRight();
	const user = useProfile();
	const { width } = useWindowSize();

	const isReader = organization?.type === organizationTypes.NARRATOR;

	const [startDate, setStartDate] = useState();
	const [endDate, setEndDate] = useState();
	const [openFilterMenu, setOpenFilterMenu] = useState(false);
	const [sessionId, setSessionId] = useState(null);
	const [viewType, setViewType] = useState("");
	const [resourceType, setResourceType] = useState(!isReader ? "studios" : "productions");
	const [movingEvent, setMovingEvent] = useState();
	const [isEditing, setIsEditing] = useState(false);
	const [openBookingAssistant, setOpenBookingAssistant] = useState(!!productionId);
	const [openNotificationModal, setOpenNotificationModal] = useState(false);
	const [openAvailabilityModal, setOpenAvailabilityModal] = useState(false);
	const [modifiedSessions, setModifiedSessions] = useState(null);
	const [createSessionData, setCreateSessionData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [showAllResources, setShowAllResources] = useState(false);
	const [resourcesOpen, setResourcesOpen] = useState(true);

	const [production] = useDocumentData(
		createSessionData && db.collection("productions").doc(createSessionData.productionId),
	);

	// Filtering
	const filterForm = useCalendarFilterForm({ user, organization });

	const { views, slotTimeFormat, customButton } = useFullCalendarProps(setOpenFilterMenu);

	const onClickFilter = () => {
		setOpenFilterMenu((openFilterMenu) => !openFilterMenu);
	};

	// Sessions
	const [sessions, filteredSessions] = useCalendarSessions({
		startDate,
		organization,
		endDate,
		modifiedSessions,
		viewType,
		filters: filterForm.filters,
	});

	const session = sessions.find((s) => s.id === sessionId);

	// Resources
	const { resourceTypes, loading, error } = useCalendarResourceTypes({ organization, sessions });

	const { resources, getOverlappingResourceIds, checkOverlap, getResourceId, resourceColors, getSessionOrder } =
		resourceTypes[resourceType];

	const filteredResources = resources.filter((resource) => {
		if (filterForm.filters.studios.length > 0 && viewType.includes("resourceTimeline")) {
			return filterForm.filters.studios.includes(resource.id);
		} else {
			return showAllResources || filteredSessions.some((session) => getResourceId(session) === resource.id);
		}
	});

	// Events
	const events = useCalendarEvents({
		sessions: filteredSessions,
		getResourceId,
		resourceColors,
		isEditing,
		resourceType,
		getSessionOrder,
	});

	const studioTimeArray = useCalendarStudioTime(filteredSessions);

	const backgroundEvents = useCalendarBackgroundEvents({
		modifiedSession: movingEvent?.extendedProps?.session || createSessionData,
		sessions,
		checkOverlap,
		getOverlappingResourceIds,
	});

	const eventClick = ({ event }) => {
		if (createSessionData || isEditing) {
			return false;
		}

		setSessionId(event.id);
	};

	// Editing
	const onClickEdit = () => {
		setIsEditing((isEditing) => !isEditing);
	};

	const onChangeEvent = useCalendarOnChangeEvent({ setIsLoading, resourceType, setModifiedSessions });

	// Booking functions
	const onClickBook = () => {
		setOpenBookingAssistant(true);
		setIsEditing(false);
	};

	const onStartBooking = (data) => {
		setCreateSessionData(data);
		setOpenBookingAssistant(false);
		setShowAllResources(true);
		setIsEditing(true);

		if (viewType.includes("Grid")) {
			navigation.changeView(viewType.replace("timeGrid", "resourceTimeline"));
		}
	};

	const onCreateSession = useCalendarOnCreateSession({
		setIsLoading,
		createSessionData,
		sessions,
		setModifiedSessions,
	});

	const onStopBooking = () => {
		setCreateSessionData(null);
		setIsEditing(false);
		setModifiedSessions(null);
	};

	// Navigation
	const isTimelineView = viewType.includes("resourceTimeline");

	const navigation = useCalendarTimelineNavigation({ ref });

	const onDatesSet = ({ view, start, end }) => {
		setViewType(view.type);

		if (start.getTime() !== startDate?.getTime()) {
			setStartDate(start);
		}
		if (end.getTime() !== endDate?.getTime()) {
			setEndDate(end);
		}

		if (view.type === "resourceTimelineWeek" && view.type !== viewType) {
			view.calendar.scrollToTime({
				days: moment().day() - 1,
				hours: moment().hour(),
			});
		}
	};

	// Business hours
	const [bookingUsers] = useCollectionDataByIds(
		firebase,
		db.collection("users"),
		createSessionData && [...createSessionData.reader, createSessionData.recorder],
	);

	const businessHours = getCalendarBusinessHours({ users: bookingUsers.length > 0 ? bookingUsers : [user] });

	const desktop = width > 1200;

	return (
		<>
			<LoadingContext loading={loading} error={error}>
				<Section maxWidth={false}>
					<Grid>
						{openFilterMenu && desktop ? (
							<Grid.Column width={3}>
								<CalendarFilterForm form={filterForm} organization={organization} />
							</Grid.Column>
						) : (
							<CalendarFilterFormModal
								open={openFilterMenu}
								onClose={() => setOpenFilterMenu(false)}
								form={filterForm}
								organization={organization}
							/>
						)}
						<Grid.Column width={openFilterMenu && desktop ? 13 : 16}>
							<CalendarHeaderToolbar
								onOpenNotifyModal={() => setOpenNotificationModal(true)}
								onOpenAvailabilityModal={() => setOpenAvailabilityModal(true)}
								navigation={navigation}
								startDate={startDate}
								endDate={endDate}
								onClickFilter={onClickFilter}
								viewType={viewType}
								isEditing={isEditing}
								onClickEdit={onClickEdit}
								onClickBook={onClickBook}
								modifiedSessions={modifiedSessions}
								isBooking={!!createSessionData}
								onStopBooking={onStopBooking}
								setShowAllResources={setShowAllResources}
								showAllResources={showAllResources}
								isTimelineView={isTimelineView}
								filterForm={filterForm}
								resourceType={resourceType}
								disableTools={!hasRight("calendar.viewTimeline")}
							/>

							<FullCalendar
								ref={ref}
								initialView={
									hasRight("calendar.defaultTimeline") ? "resourceTimelineDay" : "timeGridWeek"
								}
								views={views}
								allDaySlot={false}
								height="70vh"
								schedulerLicenseKey="0520726615-fcs-1698239718"
								locales={[enLocale, svLocale]}
								locale={i18n.language}
								aspectRatio={false}
								plugins={[momentPlugin, resourceTimelinePlugin, interactionPlugin, timeGridPlugin]}
								headerToolbar={false}
								resourceAreaHeaderContent={
									!isReader ? (
										<CalendarResourceHeader
											t={t}
											resourceType={resourceType}
											setResourceType={setResourceType}
											showAllResources={showAllResources}
											setShowAllResources={setShowAllResources}
											resourcesOpen={resourcesOpen}
											setResourcesOpen={setResourcesOpen}
											disableTools={isReader}
											isBooking={!!createSessionData}
										/>
									) : (
										t("productions", "Productions")
									)
								}
								resourceLabelContent={resourceTypes[resourceType].resourceTitle}
								resourcesInitiallyExpanded={resourcesOpen}
								resourceGroupField="group"
								resources={filteredResources}
								resourceAreaWidth="18%"
								resourceOrder={resourceType !== "engineers" ? "groupSorting, title" : "title"}
								firstDay={1}
								scrollTimeReset={false}
								navLinks
								navLinkDayClick={isReader ? "timeGridDay" : "resourceTimelineDay"}
								customButtons={customButton}
								selectable={!!createSessionData && isTimelineView}
								select={onCreateSession}
								events={[...events, ...backgroundEvents]}
								eventClick={eventClick}
								eventChange={onChangeEvent}
								eventDragStart={({ event }) => setMovingEvent(event)}
								eventDragStop={() => setMovingEvent(null)}
								eventResizeStart={({ event }) => setMovingEvent(event)}
								eventResizeStop={() => setMovingEvent(null)}
								eventOrder="order,start,-duration,allDay,title"
								eventOrderStrict
								displayEventTime
								eventTimeFormat={slotTimeFormat}
								businessHours={businessHours}
								eventOverlap={false}
								eventAllow={({ start, end, resource }) => {
									const isOverlapping = backgroundEvents
										.filter((backgroundEvent) => backgroundEvent.resourceIds.includes(resource.id))
										.some((event) => event.start < end && start < event.end);

									return !isOverlapping;
								}}
								eventContent={(props) => {
									return (
										<CalendarEventContent
											isTimelineView={isTimelineView}
											modifiedSessions={modifiedSessions}
											setModifiedSessions={setModifiedSessions}
											{...props}
										/>
									);
								}}
								moreLinkClick={isReader ? "timeGridDay" : "resourceTimelineDay"}
								moreLinkContent={CalendarMoreLinkContent}
								nowIndicator
								datesSet={onDatesSet}
							/>

							{!isReader && (
								<Flex style={{ justifyContent: "flex-start", gap: 10, marginTop: 10 }}>
									{studioTimeArray.map(({ title, time }) => (
										<Label key={title} style={{ marginBottom: 0 }}>
											{title}:<Label.Detail>{msToTime(time, false)}</Label.Detail>
										</Label>
									))}
								</Flex>
							)}
						</Grid.Column>
					</Grid>
				</Section>
			</LoadingContext>

			{session && (
				<CalendarSessionModal
					session={session}
					onClose={() => {
						setSessionId(null);
					}}
					sessions={sessions}
					setCreateSessionData={setCreateSessionData}
				/>
			)}

			{openBookingAssistant && (
				<CalendarBookingAssistant
					organization={organization}
					defaultValues={createSessionData}
					setOpenBookingAssistant={setOpenBookingAssistant}
					onStartBooking={onStartBooking}
					defaultProductionId={productionId}
					onClose={() => setOpenBookingAssistant(false)}
				/>
			)}

			{openNotificationModal && (
				<CalendarNotificationModal
					onStopBooking={onStopBooking}
					modifiedSessions={modifiedSessions}
					onClose={() => setOpenNotificationModal(false)}
				/>
			)}

			{openAvailabilityModal && <CalendarAvailabilityForm onClose={() => setOpenAvailabilityModal(false)} />}

			{isLoading && (
				<Dimmer active inverted>
					<Loader inverted size="large" />
				</Dimmer>
			)}

			{createSessionData && production && (
				<CalendarToast
					production={production}
					createSessionData={createSessionData}
					bookingUsers={bookingUsers}
				/>
			)}
		</>
	);
}
